import { BusinessObjectBase, ModelBase, ObservableCollection, QueryBus } from '@skylab-shared/core';
import { DocumentPaymentListItemModel, SaleDocumentKind } from '@skylab-shared/dataModel';
import { addDays, getMinimalOfDates } from '@skylab-shared/utils';
import { DateIncludedInAccountingQuery } from '../../accountancy/queries/date-included-in-accounting.query-base';
import { ContextKeys } from '../../common/context-keys';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';


interface ModelPart extends ModelBase {
    cashBasis: boolean;
    issueDate: Date;
    communityTransactionId: number;
    payments: ObservableCollection<DocumentPaymentListItemModel>;
    saleDate?: Date;
    kind: SaleDocumentKind;
    taxLiabilityDate: Date;
}

export abstract class TaxLiabilityDateMixin<TModel extends ModelPart> extends BusinessObjectBase<TModel>  {
    protected queryBus: QueryBus;

    public recalculateTaxLiabilityDate(): boolean {
        let newTaxLiabilityDate: Date | null = null;
        let changed = false;

        const issueDate = this.model.issueDate;
        const saleDate = this.model.saleDate;
        const firstPaymentDate = this.getFirstPaymentDate();

        const { id: skId } = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'SK' });
        const { id: wdtId } = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'WDT' });

        if (this.model.kind === SaleDocumentKind.AdvanceInvoice && !saleDate) {
            newTaxLiabilityDate = getMinimalOfDates(addDays(issueDate, this.model.cashBasis ? 180 : 0), firstPaymentDate);
        } else if (this.model.communityTransactionId === skId) {
            if (this.model.cashBasis) {
                const max = addDays(saleDate!, 180);
                newTaxLiabilityDate = getMinimalOfDates(max, firstPaymentDate);
            } else {
                newTaxLiabilityDate = getMinimalOfDates(saleDate, firstPaymentDate);
            }
        } else if (this.model.communityTransactionId === wdtId) {
            newTaxLiabilityDate = getMinimalOfDates(issueDate, firstPaymentDate);
        } else {
            newTaxLiabilityDate = getMinimalOfDates(saleDate, firstPaymentDate);
        }

        if (this.model.taxLiabilityDate?.getTime() !== newTaxLiabilityDate?.getTime()) {
            changed = true;
        }

        this.model.taxLiabilityDate = newTaxLiabilityDate!;

        return changed;
    }

    public async isTaxLiabilityDateIncludedInAccountancyAsync(): Promise<boolean> {
        if (!this.model.taxLiabilityDate) return false;

        return await this.localContext
            .getAsync(ContextKeys.dateIncludedInAccounting(this.model.taxLiabilityDate),
            () => this.queryBus.queryAsync(new DateIncludedInAccountingQuery(this.model.taxLiabilityDate)));
    }

    private getFirstPaymentDate(): Date {
        const [firstPayment] = this.model.payments
            .asArray()
            .sort((p1, p2) => p1.date.getTime() - p2.date.getTime());
        return firstPayment?.date || null;
    }
}
